import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Send, 
  MessageSquare, 
  Mail, 
  Phone, 
  Calendar,
  TrendingUp,
  TrendingDown,
  Activity
} from 'lucide-react';
import { Button } from '@vaiu/ui/button';
import { Badge } from '@vaiu/ui/badge';
import { Separator } from '@vaiu/ui/separator';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@vaiu/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@vaiu/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { Contact, Transaction, platformApi } from '@/services/platformApi';
import { CurrencyDisplay } from '@vaiu/ui';
import { currencyService } from '@/services/currencyService';
import { LoadingSpinner } from '@vaiu/ui';
import ContactTransactions from './ContactTransactions';

interface ContactProfileProps {
  contact: Contact;
  onSendMoney: () => void;
  merchantProfile?: any; // MerchantProfile from cache
  onViewMessages?: (contact: Contact, transactionId?: string) => void;
  onBack?: () => void; // Callback for back navigation
}

const ContactProfile: React.FC<ContactProfileProps> = ({
  contact,
  onSendMoney,
  merchantProfile: cachedMerchantProfile,
  onViewMessages,
  onBack
}) => {
  const navigate = useNavigate();
  // Use cached merchant profile only - no individual fetching to avoid rate limits
  const merchantProfile = cachedMerchantProfile || null;
  
  // Determine display name: merchant business name for business contacts, or contact name
  const getDisplayName = () => {
    if (contact.relationship === 'business' && merchantProfile?.businessName) {
      return merchantProfile.businessName;
    }
    return contact.name;
  };
  const { toast } = useToast();
  const { user } = useAuth();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  
  // Get user's default currency, fallback to USD
  // Ensure it's always a string (handle case where currency might be an object)
  const userCurrency = (typeof user?.currency === 'string' ? user.currency : 'USD') || 'USD';

  const handleViewMessages = () => {
    if (onViewMessages) {
      // Use provided handler
      onViewMessages(contact);
    } else {
      // Default behavior: navigate to messages page
      // If contact has transactions, go to most recent
      // Otherwise, go to direct message
      if (contact.transactions && contact.transactions.length > 0) {
        // Get most recent transaction ID
        const recentTransaction = contact.transactions[0];
        const transactionId = typeof recentTransaction === 'string' 
          ? recentTransaction 
          : recentTransaction.id || recentTransaction._id || recentTransaction.key;
        
        if (transactionId) {
          navigate(`/messages?transactionId=${transactionId}&contactId=${contact.id}`);
        } else {
          navigate(`/messages?contactId=${contact.id}`);
        }
      } else {
        // Direct message (no transactions)
        navigate(`/messages?contactId=${contact.id}`);
      }
    }
  };

  useEffect(() => {
    if (contact.id) {
      // Always fetch full transaction details from transactions API
      // Note: contact.transactions from contacts API only contains transaction IDs, not full objects
      // We need full transaction details for proper display and calculation
      fetchContactTransactions();
    }
  }, [contact.id]);

  const fetchContactTransactions = async () => {
    try {
      setLoadingTransactions(true);
      
      // Extract transaction IDs from contact.transactions (contacts API returns IDs, not full objects)
      const contactTransactionIds = contact.transactions 
        ? contact.transactions.map((t: any) => {
            // Handle both ID strings and transaction objects with IDs
            if (typeof t === 'string') return t;
            return t.id || t._id || t.key || t;
          }).filter(Boolean)
        : [];
      
      let contactTransactions: Transaction[] = [];
      
      // If we have transaction IDs from contacts API, use batch endpoint (more efficient)
      if (contactTransactionIds.length > 0) {
        try {
          console.log('📦 [ContactProfile] Using batch endpoint for transaction IDs:', {
            count: contactTransactionIds.length,
            contactId: contact.id
          });
          
          const batchResponse = await platformApi.transactions.getTransactionsBatch(contactTransactionIds);
          contactTransactions = batchResponse.transactions || [];
          
          console.log('✅ [ContactProfile] Batch fetch result:', {
            requested: batchResponse.requested,
            found: batchResponse.found,
            transactions: contactTransactions.length
          });
        } catch (batchError) {
          console.warn('⚠️ [ContactProfile] Batch fetch failed, falling back to counterpartyId filter:', batchError);
          // Fall through to counterpartyId method below
        }
      }
      
      // Fallback: If batch didn't work or no transaction IDs available, use counterpartyId filter
      if (contactTransactions.length === 0) {
        const counterpartyId = contact.merchantId || contact.id;
        
        const response = await platformApi.getTransactions({
          completedLimit: 200, // Get enough completed transactions to calculate accurate totals
          counterpartyId: counterpartyId, // Filter by counterparty/contact ID (server-side filtering)
        });
        
        // Use only completed transactions (contacts don't have pending transactions)
        const allTransactions = [
          ...(response.completed || []),
          ...(response.transactions || []) // Fallback to combined transactions array
        ];
        
        // Remove duplicates by transaction ID
        const uniqueTransactions = Array.from(
          new Map(allTransactions.map(t => [t.id || t.key || t._id, t])).values()
        );
        
        // Filter to match this contact
        contactTransactions = uniqueTransactions.filter(
          t => {
            const transactionId = t.id || t.key || t._id;
            if (!transactionId) return false;
            
            // If we have transaction IDs from contact, match by ID first
            if (contactTransactionIds.length > 0) {
              if (contactTransactionIds.includes(transactionId)) {
                return true;
              }
            }
            
            // Match by contact ID (counterparty ID) - use counterpartyId field if available
            if (t.counterpartyId === contact.id || 
                t.counterparty?.id === contact.id || 
                t.recipientId === contact.id ||
                t.originator === contact.id) {
              return true;
            }
            
            // Match by merchant ID for business contacts
            if (contact.merchantId && (
                t.counterpartyId === contact.merchantId ||
                t.counterparty?.id === contact.merchantId ||
                t.recipientId === contact.merchantId ||
                t.originator === contact.merchantId
            )) {
              return true;
            }
            
            return false;
          }
        );
      }
      
      console.log('🔍 [ContactProfile] Final transactions:', {
        count: contactTransactions.length,
        contactId: contact.id,
        contactName: contact.name,
        merchantId: contact.merchantId,
        contactTotalSent: contact.totalSent,
        contactTotalReceived: contact.totalReceived,
        contactTotalTransactions: contact.totalTransactions,
        sampleTransactions: contactTransactions.slice(0, 3).map(t => ({
          id: t.id,
          type: t.type,
          amount: t.amount,
          currency: t.currency,
          counterparty: t.counterparty,
          description: t.description || t.message
        }))
      });
      
      setTransactions(contactTransactions);
    } catch (error) {
      console.error('Failed to fetch contact transactions:', error);
      setTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getRelationshipInfo = () => {
    const relationshipMap = {
      frequent: {
        label: 'Frequent Contact',
        description: 'You transact with this person regularly',
        color: 'text-green-600'
      },
      recent: {
        label: 'Recent Contact',
        description: 'You\'ve transacted recently',
        color: 'text-blue-600'
      },
      business: {
        label: 'Business Contact',
        description: 'Business or merchant account',
        color: 'text-purple-600'
      },
      infrequent: {
        label: 'Contact',
        description: 'Limited transaction history',
        color: 'text-gray-600'
      }
    };

    return relationshipMap[contact.relationship] || relationshipMap.infrequent;
  };

  const relationshipInfo = getRelationshipInfo();

  // Calculate totals grouped by currency - prefer calculated totals from transactions when available
  const calculateTotals = React.useMemo(() => {
    // Group totals by currency
    const totalsByCurrency: Record<string, { sent: number; received: number }> = {};

    // Calculate from transactions, grouping by currency (most accurate source when available)
    if (transactions.length > 0) {
      transactions.forEach(transaction => {
        const amount = typeof transaction.amount === 'string' 
          ? parseFloat(transaction.amount) 
          : transaction.amount || 0;
        
        if (isNaN(amount) || amount === 0) {
          console.log('⚠️ [ContactProfile] Skipping transaction with invalid amount:', {
            id: transaction.id,
            amount: transaction.amount,
            type: transaction.type
          });
          return;
        }
        
        // Get transaction currency, fallback to user's currency
        const txCurrency = transaction.currency?.toUpperCase() || 
                          transaction.data?.currency?.toUpperCase() || 
                          userCurrency;
        
        // Initialize currency totals if not exists
        if (!totalsByCurrency[txCurrency]) {
          totalsByCurrency[txCurrency] = { sent: 0, received: 0 };
        }
        
        // Handle different transaction type formats
        const type = transaction.type?.toLowerCase() || '';
        
        // Determine if this transaction involves the contact as counterparty
        // Match by contact ID (counterparty ID, recipient ID, or originator)
        const isCounterparty = transaction.counterpartyId === contact.id ||
                               transaction.counterparty?.id === contact.id || 
                               transaction.recipientId === contact.id ||
                               transaction.originator === contact.id ||
                               // Match by merchant ID if contact is a merchant
                               (contact.merchantId && (
                                 transaction.counterpartyId === contact.merchantId ||
                                 transaction.counterparty?.id === contact.merchantId ||
                                 transaction.recipientId === contact.merchantId ||
                                 transaction.originator === contact.merchantId
                               )) ||
                               // Match by counterparty name (case-insensitive)
                               (transaction.counterparty?.name && contact.name &&
                                transaction.counterparty.name.toLowerCase() === contact.name.toLowerCase()) ||
                               // Match by recipient name (case-insensitive)
                               (transaction.recipientName && contact.name &&
                                transaction.recipientName.toLowerCase() === contact.name.toLowerCase());
        
        if (isCounterparty) {
          // Determine direction: if we're the originator and contact is recipient, we sent
          // If we're the recipient and contact is originator, we received
          const userId = user?._id || user?.id;
          const isOriginator = transaction.originator === userId || transaction.originatorId === userId;
          const isRecipient = transaction.recipientId === userId || transaction.recipient === userId;
          
          // If we sent TO the contact (send/debit/transfer, or we're originator and contact is recipient)
          if (type === 'send' || type === 'debit' || type === 'transfer' || 
              (isOriginator && transaction.recipientId === contact.id) ||
              (isOriginator && contact.merchantId && transaction.recipientId === contact.merchantId)) {
            totalsByCurrency[txCurrency].sent += Math.abs(amount);
          } 
          // If we received FROM the contact (receive/credit/deposit/redeem, or we're recipient and contact is originator)
          else if (type === 'receive' || type === 'credit' || type === 'deposit' || type === 'redeem' ||
                   (isRecipient && transaction.originator === contact.id) ||
                   (isRecipient && contact.merchantId && transaction.originator === contact.merchantId)) {
            totalsByCurrency[txCurrency].received += Math.abs(amount);
          }
        } else {
          console.log('⚠️ [ContactProfile] Transaction not matched to contact:', {
            transactionId: transaction.id,
            transactionType: type,
            transactionCounterparty: transaction.counterparty,
            transactionCounterpartyId: transaction.counterpartyId,
            transactionRecipientId: transaction.recipientId,
            transactionOriginator: transaction.originator,
            contactId: contact.id,
            contactMerchantId: contact.merchantId,
            contactName: contact.name,
            userId: user?.id
          });
        }
      });
      
      console.log('🔍 [ContactProfile] Calculated totals by currency:', {
        transactionsCount: transactions.length,
        totalsByCurrency,
        contactTotalSent: contact.totalSent,
        contactTotalReceived: contact.totalReceived,
        userCurrency
      });
    }
    
    // Fallback: if no transactions calculated but contact totals exist, use them for user's currency
    if (Object.keys(totalsByCurrency).length === 0 && (contact.totalSent > 0 || contact.totalReceived > 0)) {
      totalsByCurrency[userCurrency] = {
        sent: contact.totalSent || 0,
        received: contact.totalReceived || 0
      };
    }

    // Get totals for user's currency (primary display)
    const primaryTotals = totalsByCurrency[userCurrency] || { sent: 0, received: 0 };
    
    // Final fallback: if still zero but contact has transaction count, use contact totals
    if (primaryTotals.sent === 0 && primaryTotals.received === 0 && contact.totalTransactions > 0) {
      primaryTotals.sent = contact.totalSent || 0;
      primaryTotals.received = contact.totalReceived || 0;
      if (!totalsByCurrency[userCurrency]) {
        totalsByCurrency[userCurrency] = primaryTotals;
      }
    }

    return {
      sent: primaryTotals.sent,
      received: primaryTotals.received,
      byCurrency: totalsByCurrency || {}
    };
  }, [contact.totalSent, contact.totalReceived, contact.totalTransactions, contact.id, contact.merchantId, transactions, userCurrency]);

  // Calculate token counts by type (cash vs invoice) and currency
  const tokenCountsByType = React.useMemo(() => {
    const counts: Record<string, { cash: number; invoice: number }> = {};
    
    if (transactions.length > 0) {
      transactions.forEach(transaction => {
        // Get transaction currency
        const txCurrency = transaction.currency?.toUpperCase() || 
                          transaction.data?.currency?.toUpperCase() || 
                          userCurrency;
        
        // Initialize currency counts if not exists
        if (!counts[txCurrency]) {
          counts[txCurrency] = { cash: 0, invoice: 0 };
        }
        
        // Determine if this transaction involves the contact as counterparty
        // Match by contact ID (counterparty ID, recipient ID, or originator)
        const isCounterparty = transaction.counterpartyId === contact.id ||
                               transaction.counterparty?.id === contact.id || 
                               transaction.recipientId === contact.id ||
                               transaction.originator === contact.id ||
                               // Match by merchant ID if contact is a merchant
                               (contact.merchantId && (
                                 transaction.counterpartyId === contact.merchantId ||
                                 transaction.counterparty?.id === contact.merchantId ||
                                 transaction.recipientId === contact.merchantId ||
                                 transaction.originator === contact.merchantId
                               )) ||
                               // Match by counterparty name (case-insensitive)
                               (transaction.counterparty?.name && contact.name &&
                                transaction.counterparty.name.toLowerCase() === contact.name.toLowerCase()) ||
                               // Match by recipient name (case-insensitive)
                               (transaction.recipientName && contact.name &&
                                transaction.recipientName.toLowerCase() === contact.name.toLowerCase());
        
        if (isCounterparty) {
          // Determine token type: invoice or cash
          const isInvoice = transaction.isInvoice === true || 
                           transaction.tokenType === 'invoice' ||
                           (transaction.tokenType && transaction.tokenType.toLowerCase().includes('invoice'));
          
          if (isInvoice) {
            counts[txCurrency].invoice += 1;
          } else {
            // Default to cash if not explicitly invoice
            counts[txCurrency].cash += 1;
          }
        }
      });
    }
    
    return counts;
  }, [transactions, contact.id, contact.merchantId, userCurrency]);

  return (
    <div className="space-y-4 sm:space-y-6">
          {/* Contact Header */}
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <div className="flex items-start space-x-3 sm:space-x-4 w-full sm:w-auto min-w-0 flex-1">
              <Avatar className="h-16 w-16 sm:h-20 sm:w-20 flex-shrink-0">
                <AvatarImage src={
                  contact.relationship === 'business' && merchantProfile?.logo
                    ? merchantProfile.logo
                    : contact.avatar || `https://avatar.vercel.sh/${contact.email || contact.name}`
                } />
                <AvatarFallback className="bg-primary/10 text-primary font-medium text-lg sm:text-xl">
                  {getInitials(getDisplayName())}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1 min-w-0 overflow-hidden">
                <div className="mb-2">
                  <h2 className="text-xl sm:text-2xl font-bold break-words">{getDisplayName()}</h2>
                </div>
                
                <div className="flex flex-col items-start gap-2 text-xs sm:text-sm text-muted-foreground">
                  {contact.email && (
                    <div className="flex items-start space-x-1 w-full min-w-0">
                      <Mail className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 mt-0.5" />
                      <span className="break-words overflow-wrap-anywhere word-break-break-word">{contact.email}</span>
                    </div>
                  )}
                  {contact.phoneNumber && (
                    <div className="flex items-center space-x-1 w-full min-w-0">
                      <Phone className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="break-words">{contact.phoneNumber}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex flex-row sm:flex-col gap-2 w-full sm:w-auto sm:flex-shrink-0 mt-2 sm:mt-0">
              <Button onClick={onSendMoney} className="flex-1 sm:flex-none whitespace-nowrap text-sm sm:text-base w-full sm:w-auto">
                <Send className="w-4 h-4 mr-2" />
                Send Money
              </Button>
              {/* Always show Messages button */}
              <Button 
                variant="outline" 
                className="flex-1 sm:flex-none whitespace-nowrap text-sm sm:text-base w-full sm:w-auto"
                onClick={handleViewMessages}
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                {contact.hasConversations && contact.conversationCount 
                  ? `Messages (${contact.conversationCount})`
                  : 'Message'}
              </Button>
            </div>
          </div>

          <Separator />

          {/* Transaction Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center space-x-2 min-w-0">
                  <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm font-medium break-words">Transactions</p>
                    <p className="text-xl sm:text-2xl font-bold break-words">{contact.totalTransactions}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center space-x-2 min-w-0">
                  <TrendingDown className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <p className="text-xs sm:text-sm font-medium break-words">Paid</p>
                    <p className="text-xl sm:text-2xl font-bold break-words">
                      <CurrencyDisplay amount={Number(calculateTotals.sent) || 0} currency={userCurrency} currencyService={currencyService} size="lg" />
                    </p>
                    {calculateTotals.byCurrency && Object.keys(calculateTotals.byCurrency).length > 1 && (
                      <p className="text-xs text-muted-foreground mt-1 break-words">
                        {Object.entries(calculateTotals.byCurrency)
                          .filter(([curr]) => curr !== userCurrency)
                          .filter(([, totals]) => totals && typeof totals === 'object' && 'sent' in totals)
                          .map(([curr, totals]) => {
                            const sentAmount = totals?.sent;
                            return sentAmount && sentAmount > 0 ? (
                              <span key={curr} className="mr-2">
                                <CurrencyDisplay amount={Number(sentAmount)} currency={String(curr)} currencyService={currencyService} size="sm" />
                              </span>
                            ) : null;
                          })
                          .filter(Boolean)}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center space-x-2 min-w-0">
                  <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <p className="text-xs sm:text-sm font-medium break-words">Received</p>
                    <p className="text-xl sm:text-2xl font-bold break-words">
                      <CurrencyDisplay amount={Number(calculateTotals.received) || 0} currency={userCurrency} currencyService={currencyService} size="lg" />
                    </p>
                    {calculateTotals.byCurrency && Object.keys(calculateTotals.byCurrency).length > 1 && (
                      <p className="text-xs text-muted-foreground mt-1 break-words">
                        {Object.entries(calculateTotals.byCurrency)
                          .filter(([curr]) => curr !== userCurrency)
                          .filter(([, totals]) => totals && typeof totals === 'object' && 'received' in totals)
                          .map(([curr, totals]) => {
                            const receivedAmount = totals?.received;
                            return receivedAmount && receivedAmount > 0 ? (
                              <span key={curr} className="mr-2">
                                <CurrencyDisplay amount={Number(receivedAmount)} currency={String(curr)} currencyService={currencyService} size="sm" />
                              </span>
                            ) : null;
                          })
                          .filter(Boolean)}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Token Counts by Type and Currency */}
          {Object.keys(tokenCountsByType).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base sm:text-lg">Token Counts</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Cash and invoice tokens by currency</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(tokenCountsByType).map(([currency, counts]) => (
                    <div key={currency} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4 p-3 bg-muted/50 rounded-lg overflow-hidden">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-medium text-sm sm:text-base break-words">{currency}</span>
                      </div>
                      <div className="flex items-center flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm w-full sm:w-auto">
                        <div className="flex items-center gap-1">
                          <span className="text-muted-foreground">Cash:</span>
                          <span className="font-medium">{counts.cash}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-muted-foreground">Invoice:</span>
                          <span className="font-medium">{counts.invoice}</span>
                        </div>
                        <div className="flex items-center gap-1 sm:ml-2 sm:pl-2 sm:border-l border-t sm:border-t-0 pt-2 sm:pt-0 w-full sm:w-auto">
                          <span className="text-muted-foreground">Total:</span>
                          <span className="font-medium">{counts.cash + counts.invoice}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Contact Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-base sm:text-lg">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                <span className="break-words">Contact Timeline</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs sm:text-sm">
                <div className="min-w-0">
                  <p className="font-medium mb-1">First Transaction</p>
                  <p className="text-muted-foreground break-words">{formatDate(contact.firstTransactionDate)}</p>
                </div>
                <div className="min-w-0">
                  <p className="font-medium mb-1">Last Transaction</p>
                  <p className="text-muted-foreground break-words">{formatDate(contact.lastTransactionDate)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Transaction History Tabs */}
          <Tabs defaultValue="transactions" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="transactions">
                Transaction History ({contact.totalTransactions})
              </TabsTrigger>
              <TabsTrigger value="conversations" disabled={!contact.hasConversations}>
                Conversations ({contact.conversationCount})
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="transactions" className="mt-4">
              {loadingTransactions ? (
                <div className="py-8">
                  <LoadingSpinner text="Loading transactions..." />
                </div>
              ) : (
                <ContactTransactions
                  transactions={transactions}
                  contactName={contact.name}
                />
              )}
            </TabsContent>
            
            <TabsContent value="conversations" className="mt-4">
              <div className="text-center py-8">
                <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Conversations</h3>
                <p className="text-muted-foreground">
                  Message history with {contact.name} will appear here
                </p>
              </div>
            </TabsContent>
          </Tabs>
    </div>
  );
};

export default ContactProfile; 